import { useCallback, useEffect, useRef, useState } from "react";
import { getSprintDevs, getSprintGrafico, getSprintDados, clearCache } from "../api/sprint.api";
import GraficoDevs from "../components/GraficoDevs";
import TicketDrawer from "../components/TicketDrawer";

const REFRESH_SEC = 40;

const FECHADOS_STATUS = [
  "Fechado", "Resolvido", "Reteste", "Em Teste", "Feedback",
  "Aguardando Testes", "Aguardando Versão", "Ajuste Especificação",
];
const isClosed = (s) => FECHADOS_STATUS.includes(s);

/* ── Visual helpers ──────────────────────────────────────────────── */

const DEV_AVATAR_MAP = [
  { match: "guilherme", bg: "#712B13", color: "#F5C4B3" },
  { match: "paulo",     bg: "#0C447C", color: "#B5D4F4" },
  { match: "marcelo",   bg: "#085041", color: "#9FE1CB" },
  { match: "rudi",      bg: "#3C3489", color: "#CECBF6" },
];
function devAvatarCores(nome) {
  if (!nome) return { bg: "#2C2C2A", color: "#B4B2A9" };
  const low = nome.toLowerCase();
  for (const m of DEV_AVATAR_MAP) if (low.includes(m.match)) return m;
  return { bg: "#2C2C2A", color: "#B4B2A9" };
}
function devAvatarInitial(nome) {
  if (!nome) return "?";
  const parts = nome.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : parts[0][0].toUpperCase();
}

/* ── Página ──────────────────────────────────────────────────────── */

export default function Developers() {
  const [devs,       setDevs]       = useState([]);
  const [grafico,    setGrafico]    = useState(null);
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [countdown,  setCountdown]  = useState(REFRESH_SEC);
  const [drawer,     setDrawer]     = useState({ open: false, fetchId: 0 });
  const [hovFiltre,  setHovFiltre]  = useState(null);
  const [hovRow,     setHovRow]     = useState(null);
  const [hovCard,    setHovCard]    = useState(null);
  const loadRef = useRef(null);

  const openDrawer  = useCallback((cfg) => setDrawer(prev => ({ ...cfg, open: true, fetchId: prev.fetchId + 1 })), []);
  const closeDrawer = useCallback(() => setDrawer(prev => ({ ...prev, open: false })), []);

  const isProg = (t) => String(t.programado) === "1" || t.programado === true;
  const fetchDev = (nome) => () => getSprintDados().then(d => {
    let rows = d.filter(t => t.desenvolvedor === nome);
    return filtroTipo === "programados" ? rows.filter(isProg) : rows;
  });
  const fetchDevAbertos = (nome) => () => getSprintDados().then(d => {
    let rows = d.filter(t => t.desenvolvedor === nome && !isClosed(t.status));
    return filtroTipo === "programados" ? rows.filter(isProg) : rows;
  });
  const fetchDevFechados = (nome) => () => getSprintDados().then(d => {
    let rows = d.filter(t => t.desenvolvedor === nome && isClosed(t.status));
    return filtroTipo === "programados" ? rows.filter(isProg) : rows;
  });

  function handleBarClick(nome, tipo) {
    if (tipo === "abertos") {
      openDrawer({ title: `${nome} — Abertos`, subtitle: "Tickets ainda em andamento", color: "#f87171", fetchTickets: fetchDevAbertos(nome) });
    } else {
      openDrawer({ title: `${nome} — Fechados`, subtitle: "Tickets concluídos na sprint", color: "#4ade80", fetchTickets: fetchDevFechados(nome) });
    }
  }

  async function load(isRefresh = false) {
    try {
      if (isRefresh) clearCache();
      const [d, g] = await Promise.all([getSprintDevs(), getSprintGrafico()]);
      setDevs(d?.developers || []);
      setGrafico(g);
    } catch (e) {
      console.error(e);
    }
  }

  loadRef.current = load;

  function handleRefresh() {
    loadRef.current(true);
    setCountdown(REFRESH_SEC);
  }

  useEffect(() => {
    loadRef.current();
    setCountdown(REFRESH_SEC);
    const id = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { loadRef.current(true); return REFRESH_SEC; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const combine = (d) => filtroTipo === "programados"
    ? { total: d.programado?.total || 0, aberto: d.programado?.aberto || 0, fechado: d.programado?.fechado || 0 }
    : { total: (d.programado?.total||0)+(d.emergencia?.total||0), aberto: (d.programado?.aberto||0)+(d.emergencia?.aberto||0), fechado: (d.programado?.fechado||0)+(d.emergencia?.fechado||0) };

  const graficoDados = filtroTipo === "programados" && devs.length > 0
    ? { labels: devs.map(d => d.nome), abertos: devs.map(d => d.programado?.aberto||0), fechados: devs.map(d => d.programado?.fechado||0) }
    : grafico;

  const calcPct = (d) => {
    const c = combine(d);
    return c.total ? Math.round((c.fechado / c.total) * 100) : 0;
  };

  const ranking = [...devs]
    .map((d) => ({ ...d, combined: combine(d), pct: calcPct(d) }))
    .sort((a, b) => b.pct - a.pct);

  const totalGeral   = ranking.reduce((s, d) => s + d.combined.total,  0);
  const abertoGeral  = ranking.reduce((s, d) => s + d.combined.aberto, 0);
  const fechadoGeral = ranking.reduce((s, d) => s + d.combined.fechado, 0);
  const pctGeral     = totalGeral ? Math.round((fechadoGeral / totalGeral) * 100) : 0;

  const devColor = (p) => p >= 90 ? "#1D9E75" : p >= 70 ? "#EF9F27" : "#E24B4A";

  return (
    <div style={page}>

      {/* ── Header ── */}
      <header style={header}>
        <div>
          <h1 style={h1}>Desenvolvedores</h1>
          <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
            {[["todos","Todos"],["programados","Somente Programados"]].map(([val, label]) => {
              const isAtivo = filtroTipo === val;
              const isHov   = hovFiltre === val;
              return (
                <button
                  key={val}
                  onClick={() => setFiltroTipo(val)}
                  onMouseEnter={() => setHovFiltre(val)}
                  onMouseLeave={() => setHovFiltre(null)}
                  style={{
                    padding: "5px 14px", borderRadius: 99,
                    fontSize: 12, fontWeight: 500, cursor: "pointer",
                    transition: "all 0.15s",
                    border: isAtivo ? "none" : `0.5px solid ${isHov ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.12)"}`,
                    background: isAtivo ? "#534AB7" : "transparent",
                    color: isAtivo ? "#EEEDFE" : isHov ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.4)",
                  }}
                >{label}</button>
              );
            })}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={handleRefresh}
            title="Atualizar agora"
            style={refreshBtn}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(96,165,250,0.18)"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.45)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(96,165,250,0.08)"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.2)"; }}
          >
            ↻
          </button>
          <CountdownRing seconds={countdown} total={REFRESH_SEC} />
        </div>
      </header>

      {/* ── KPI Cards ── */}
      <div style={kpiGrid}>
        <KpiCard label="Total de Tickets" value={totalGeral}         color="#5F5E5A" />
        <KpiCard label="Abertos"          value={abertoGeral}        color="#E24B4A" />
        <KpiCard label="Fechados"         value={fechadoGeral}       color="#1D9E75" />
        <KpiCard
          label="Conclusão Geral"
          value={`${pctGeral}%`}
          color={devColor(pctGeral)}
          bar={<MiniBar value={pctGeral} color={devColor(pctGeral)} />}
        />
      </div>

      {/* ── Corpo principal ── */}
      <div style={bodyGrid}>

        {/* ── Coluna esquerda: gráfico + tabela ── */}
        <div style={glass}>
          <SectionTitle>Tickets por Desenvolvedor</SectionTitle>
          <div style={{ flex: 1, minHeight: 0 }}>
            <GraficoDevs data={graficoDados} onBarClick={handleBarClick} />
          </div>

          {graficoDados && (
            <div style={valTable}>
              {/* Cabeçalho */}
              <div style={valHeader}>
                <span style={{ ...valCell, flex: 2, textAlign: "left" }}>Desenvolvedor</span>
                <span style={valCell}>Abertos</span>
                <span style={valCell}>Fechados</span>
                <span style={valCell}>Total</span>
                <span style={valCell}>Conclusão</span>
              </div>
              {graficoDados.labels.map((label, i) => {
                const ab  = graficoDados.abertos[i]  || 0;
                const fe  = graficoDados.fechados[i] || 0;
                const tot = ab + fe;
                const pct = tot ? Math.round((fe / tot) * 100) : 0;
                const av  = devAvatarCores(label);
                const isHov = hovRow === i;
                return (
                  <div
                    key={i}
                    style={{ ...valRow, background: isHov ? "rgba(255,255,255,0.04)" : "transparent" }}
                    onMouseEnter={() => setHovRow(i)}
                    onMouseLeave={() => setHovRow(null)}
                  >
                    {/* Nome + avatar */}
                    <div
                      onClick={() => openDrawer({ title: label, subtitle: "Todos os tickets deste desenvolvedor", color: "#60a5fa", fetchTickets: fetchDev(label) })}
                      style={{ ...valCell, flex: 2, display: "flex", alignItems: "center", gap: 8, cursor: "pointer", justifyContent: "flex-start" }}
                    >
                      <div style={{ width: 24, height: 24, borderRadius: "50%", background: av.bg, color: av.color, fontSize: 10, fontWeight: 500, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {devAvatarInitial(label)}
                      </div>
                      <span style={{ fontSize: 13, color: "#E5E7EB" }}>{label}</span>
                    </div>
                    {/* Abertos */}
                    <span style={{ ...valCell, color: ab > 0 ? "#F09595" : "rgba(255,255,255,0.3)", fontWeight: 700 }}>{ab}</span>
                    {/* Fechados */}
                    <span style={{ ...valCell, color: "#97C459", fontWeight: 700 }}>{fe}</span>
                    {/* Total */}
                    <span style={{ ...valCell, color: "rgba(255,255,255,0.35)", fontWeight: 600 }}>{tot}</span>
                    {/* Conclusão: mini barra + % */}
                    <div style={{ ...valCell, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                      <div style={{ width: 60, height: 3, borderRadius: 2, background: "rgba(255,255,255,0.08)", overflow: "hidden", flexShrink: 0 }}>
                        <div style={{ height: "100%", width: `${pct}%`, background: devColor(pct), borderRadius: 2 }} />
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 500, color: devColor(pct), whiteSpace: "nowrap" }}>{pct}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Coluna direita: ranking detalhado ── */}
        <div style={{ ...glass, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <SectionTitle>Ranking — Fila por Desenvolvedor</SectionTitle>
          <div style={{ flex: 1, overflowY: "auto", paddingRight: 2 }}>
            {ranking.map((d, i) => {
              const p    = d.pct;
              const c    = devColor(p);
              const prog = d.programado || {};
              const emrg = d.emergencia || {};
              const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : null;
              const av   = devAvatarCores(d.nome);
              const progPct = prog.total ? Math.round(((prog.fechado||0)/prog.total)*100) : 0;
              const emrgPct = emrg.total ? Math.round(((emrg.fechado||0)/emrg.total)*100) : 0;
              const isHov = hovCard === i;

              return (
                <div
                  key={i}
                  style={{
                    background: isHov ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.03)",
                    border: "0.5px solid rgba(255,255,255,0.07)",
                    borderRadius: 10,
                    padding: "14px 16px",
                    marginBottom: 12,
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={() => setHovCard(i)}
                  onMouseLeave={() => setHovCard(null)}
                >
                  {/* ── Cabeçalho do card ── */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <span style={{ fontSize: 18, minWidth: 26, lineHeight: 1 }}>
                      {medal || <span style={{ color: "rgba(255,255,255,0.25)", fontSize: 13, fontWeight: 700 }}>#{i + 1}</span>}
                    </span>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: av.bg, color: av.color, fontSize: 11, fontWeight: 500, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {devAvatarInitial(d.nome)}
                    </div>
                    <span
                      onClick={() => openDrawer({ title: d.nome, subtitle: "Todos os tickets deste desenvolvedor", color: devColor(p), fetchTickets: fetchDev(d.nome) })}
                      style={{ fontSize: 14, fontWeight: 500, color: "#E5E7EB", cursor: "pointer", flex: 1 }}
                    >{d.nome}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#cbd5e1", background: "rgba(255,255,255,0.1)", padding: "2px 9px", borderRadius: 99, flexShrink: 0 }}>
                      {d.combined.total} ticket{d.combined.total !== 1 ? "s" : ""}
                    </span>
                    <span style={{ fontSize: 14, fontWeight: 500, color: c, flexShrink: 0 }}>{p}%</span>
                  </div>

                  {/* ── Breakdown: Programados ── */}
                  {(prog.total > 0) && (
                    <div style={breakdown}>
                      <div style={breakdownLabel}>
                        <span style={{ ...bdot, background: "#534AB7" }} />
                        <span style={{ color: "#AFA9EC", fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.07em" }}>Programados</span>
                        <span style={{ color: "rgba(255,255,255,0.25)", fontSize: 10, marginLeft: 4 }}>{prog.total} tickets</span>
                      </div>
                      <div style={bdStats}>
                        <BdStat label="Abertos"   value={prog.aberto  || 0} color={(prog.aberto||0)  > 0 ? "#F09595" : "rgba(255,255,255,0.25)"} />
                        <BdStat label="Fechados"  value={prog.fechado || 0} color="#97C459" />
                        <BdStat label="Conclusão" value={`${progPct}%`}      color={devColor(progPct)} />
                      </div>
                      <div style={{ height: 3, background: "rgba(255,255,255,0.08)", borderRadius: 2, overflow: "hidden", margin: "8px 0" }}>
                        <div style={{ height: "100%", width: `${progPct}%`, background: devColor(progPct), borderRadius: 2 }} />
                      </div>
                    </div>
                  )}

                  {/* ── Breakdown: Emergências ── */}
                  {(emrg.total > 0) && filtroTipo !== "programados" && (
                    <div style={breakdown}>
                      <div style={breakdownLabel}>
                        <span style={{ ...bdot, background: "#E24B4A" }} />
                        <span style={{ color: "#F09595", fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.07em" }}>Emergências</span>
                        <span style={{ color: "rgba(255,255,255,0.25)", fontSize: 10, marginLeft: 4 }}>{emrg.total} tickets</span>
                      </div>
                      <div style={bdStats}>
                        <BdStat label="Abertos"   value={emrg.aberto  || 0} color={(emrg.aberto||0)  > 0 ? "#F09595" : "rgba(255,255,255,0.25)"} />
                        <BdStat label="Fechados"  value={emrg.fechado || 0} color="#97C459" />
                        <BdStat label="Conclusão" value={`${emrgPct}%`}      color={devColor(emrgPct)} />
                      </div>
                      <div style={{ height: 3, background: "rgba(255,255,255,0.08)", borderRadius: 2, overflow: "hidden", margin: "8px 0" }}>
                        <div style={{ height: "100%", width: `${emrgPct}%`, background: devColor(emrgPct), borderRadius: 2 }} />
                      </div>
                    </div>
                  )}

                  {/* ── Pills resumo ── */}
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <Pill variant="total"    value={d.combined.total}   label={`${d.combined.total} total`} />
                    <Pill variant="abertos"  value={d.combined.aberto}  label={`${d.combined.aberto} abertos`} />
                    <Pill variant="fechados" value={d.combined.fechado} label={`${d.combined.fechado} fechados`} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>

      <TicketDrawer
        open={drawer.open}
        onClose={closeDrawer}
        title={drawer.title}
        subtitle={drawer.subtitle}
        color={drawer.color}
        fetchTickets={drawer.fetchTickets}
        fetchId={drawer.fetchId}
      />
    </div>
  );
}

/* ── Sub-components ──────────────────────────────────────────────── */

function CountdownRing({ seconds, total }) {
  const r      = 11;
  const circ   = 2 * Math.PI * r;
  const filled = circ * (seconds / total);
  const urgent = seconds <= 10;
  const color  = urgent ? "#f87171" : "#60a5fa";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <svg width={30} height={30} style={{ transform: "rotate(-90deg)", flexShrink: 0 }}>
        <circle cx={15} cy={15} r={r} fill="none" stroke="rgba(96,165,250,0.1)" strokeWidth={2.5} />
        <circle cx={15} cy={15} r={r} fill="none"
          stroke={color} strokeWidth={2.5}
          strokeDasharray={circ}
          strokeDashoffset={circ - filled}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.8s linear, stroke 0.3s" }}
        />
      </svg>
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
        <span style={{ color, fontSize: 14, fontWeight: 800, letterSpacing: -0.5 }}>
          {String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}
        </span>
        <span style={{ color: "#475569", fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5 }}>
          próx. atualiz.
        </span>
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontSize: 11, letterSpacing: "0.07em", color: "rgba(255,255,255,0.3)", textTransform: "uppercase", marginBottom: 12, flexShrink: 0 }}>
      {children}
    </div>
  );
}

function KpiCard({ label, value, color, bar }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      style={{
        ...glass,
        justifyContent: "center",
        gap: 4,
        borderLeft: `3px solid ${color}`,
        borderRadius: "0 8px 8px 0",
        background: hov ? "rgba(255,255,255,0.04)" : glass.background,
        transition: "background 0.2s",
      }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      <div style={{ fontSize: 10, letterSpacing: "0.08em", color: "rgba(255,255,255,0.35)", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      {bar}
    </div>
  );
}

function MiniBar({ value, color }) {
  return (
    <div style={{ height: 3, background: "rgba(255,255,255,0.08)", borderRadius: 2, overflow: "hidden", marginTop: 4 }}>
      <div style={{ height: "100%", width: `${value}%`, background: color, borderRadius: 2, transition: "width 0.6s" }} />
    </div>
  );
}

function BdStat({ label, value, color }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
      <span style={{ fontSize: 13, fontWeight: 700, color }}>{value}</span>
      <span style={{ fontSize: 9, color: "rgba(255,255,255,0.25)", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
    </div>
  );
}

function Pill({ variant, value, label }) {
  const isFechados = variant === "fechados";
  const isAbertos  = variant === "abertos";
  const bg = isFechados
    ? "rgba(99,153,34,0.12)"
    : isAbertos && value > 0
      ? "rgba(226,75,74,0.12)"
      : isAbertos
        ? "rgba(255,255,255,0.04)"
        : "rgba(255,255,255,0.06)";
  const color = isFechados
    ? "#97C459"
    : isAbertos && value > 0
      ? "#F09595"
      : isAbertos
        ? "rgba(255,255,255,0.25)"
        : "rgba(255,255,255,0.4)";
  return (
    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: bg, color }}>
      {label}
    </span>
  );
}

/* ── Styles ──────────────────────────────────────────────────────── */

const page = {
  display: "flex",
  flexDirection: "column",
  gap: 14,
  height: "calc(100vh - 56px)",
  overflow: "hidden",
};

const header = { display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 };
const h1     = { fontSize: 22, fontWeight: 800, color: "#f1f5f9", letterSpacing: -0.5 };

const refreshBtn = {
  width: 34, height: 34, borderRadius: 8,
  border: "1px solid rgba(96,165,250,0.2)",
  background: "rgba(96,165,250,0.08)",
  color: "#60a5fa", fontSize: 18, cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
  transition: "background 0.15s, border-color 0.15s",
  flexShrink: 0, lineHeight: 1,
};

const glass = {
  background: "rgba(11,22,42,0.72)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  border: "1px solid rgba(96,165,250,0.1)",
  borderRadius: 14,
  padding: "16px 18px",
  boxShadow: "0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)",
  display: "flex",
  flexDirection: "column",
};

const kpiGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: 12,
  flexShrink: 0,
  height: 88,
};

const bodyGrid = {
  display: "grid",
  gridTemplateColumns: "3fr 2fr",
  gap: 14,
  flex: 1,
  minHeight: 0,
};

/* Tabela */
const valTable = { marginTop: 10, flexShrink: 0, borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: 10 };
const valHeader = {
  display: "flex", gap: 8, paddingBottom: 8,
  borderBottom: "0.5px solid rgba(255,255,255,0.07)",
  marginBottom: 2,
};
const valRow = {
  display: "flex", gap: 8,
  padding: "10px 0",
  borderBottom: "0.5px solid rgba(255,255,255,0.05)",
  transition: "background 0.15s",
  borderRadius: 4,
};
const valCell = {
  flex: 1, fontSize: 10, textAlign: "center", fontWeight: 600,
  color: "rgba(255,255,255,0.25)",
  textTransform: "uppercase", letterSpacing: "0.07em",
};

/* Ranking */
const breakdown      = { background: "rgba(255,255,255,0.02)", borderRadius: 8, padding: "8px 10px", display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 };
const breakdownLabel = { display: "flex", alignItems: "center", gap: 5 };
const bdot           = { width: 6, height: 6, borderRadius: "50%", flexShrink: 0 };
const bdStats        = { display: "flex", gap: 16 };
